import React, { useEffect, useState, useCallback, useMemo } from 'react';
import axios from 'axios';
import { useAuth } from './context/AuthContext';

const ROLES = ['annotator', 'user', 'superuser'];

const UsersAdminPage = () => {
  const { user: currentUser } = useAuth();
  const currentUserId = currentUser?.id;
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [form, setForm] = useState({ username: '', email: '', password: '', role: 'user' });
  const [busy, setBusy] = useState(false);
  const [pendingRoles, setPendingRoles] = useState({});
  const [pendingActive, setPendingActive] = useState({});
  const [savingAll, setSavingAll] = useState(false);
  // Map of userId -> { token, expires_at } for reset tokens minted in this session.
  // Tokens are returned exactly once by the backend, so we keep them in memory
  // until the superuser dismisses them or reloads the page.
  const [resetTokens, setResetTokens] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const r = await axios.get('/api/users');
      setUsers(r.data.users || []);
      setPendingRoles({});
      setPendingActive({});
    } catch (e) {
      setError(e?.response?.data?.error || 'Failed to load users');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleCreate = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError('');
    try {
      const payload = { ...form, email: form.email.trim() };
      if (!payload.email) delete payload.email;
      const r = await axios.post('/api/users', payload);
      const created = r.data?.user;
      setForm({ username: '', email: '', password: '', role: 'user' });
      if (created) {
        // Append the newly created user locally instead of refetching the
        // whole list — keeps the UI snappy on large user tables.
        setUsers((prev) => [...prev, created]);
      } else {
        await load();
      }
    } catch (e) {
      setError(e?.response?.data?.error || 'Failed to create user');
    } finally {
      setBusy(false);
    }
  };

  const handleRoleSelect = (id, role) => {
    setPendingRoles((prev) => {
      const next = { ...prev };
      const u = users.find((x) => x.id === id);
      if (u && u.role === role) {
        delete next[id];
      } else {
        next[id] = role;
      }
      return next;
    });
  };

  const handleActiveToggle = (u) => {
    setPendingActive((prev) => {
      const next = { ...prev };
      const currentEffective = next[u.id] !== undefined ? next[u.id] : u.is_active;
      const newVal = !currentEffective;
      if (newVal === u.is_active) {
        delete next[u.id];
      } else {
        next[u.id] = newVal;
      }
      return next;
    });
  };

  const dirtyIds = useMemo(() => {
    const ids = new Set();
    Object.keys(pendingRoles).forEach((id) => ids.add(Number(id)));
    Object.keys(pendingActive).forEach((id) => ids.add(Number(id)));
    return ids;
  }, [pendingRoles, pendingActive]);

  const handleSaveAll = async () => {
    if (dirtyIds.size === 0) return;
    setSavingAll(true);
    setError('');
    try {
      const updates = Array.from(dirtyIds).map((id) => {
        const payload = {};
        if (pendingRoles[id] !== undefined) payload.role = pendingRoles[id];
        if (pendingActive[id] !== undefined) payload.is_active = pendingActive[id];
        return axios.patch(`/api/users/${id}`, payload);
      });
      const responses = await Promise.all(updates);
      // Merge updated rows in place rather than refetching the entire list.
      const updated = responses
        .map((res) => res?.data?.user)
        .filter(Boolean);
      if (updated.length) {
        setUsers((prev) => prev.map((u) => updated.find((x) => x.id === u.id) || u));
      }
      setPendingRoles({});
      setPendingActive({});
    } catch (e) {
      setError(e?.response?.data?.error || 'Failed to save changes');
    } finally {
      setSavingAll(false);
    }
  };

  const handleDiscardAll = () => {
    setPendingRoles({});
    setPendingActive({});
  };

  const handleResetPassword = async (id) => {
    const pw = window.prompt('Enter new password:');
    if (!pw) return;
    try {
      await axios.patch(`/api/users/${id}`, { password: pw });
    } catch (e) {
      setError(e?.response?.data?.error || 'Failed to reset password');
    }
  };

  const handleIssueResetToken = async (id) => {
    setError('');
    try {
      const r = await axios.post(`/api/users/${id}/reset-token`);
      setResetTokens((prev) => ({
        ...prev,
        [id]: { token: r.data.token, expires_at: r.data.expires_at },
      }));
      // Reflect the server-side state change (reset_requested cleared,
      // reset_token_active=true) locally without refetching.
      setUsers((prev) => prev.map((u) => (
        u.id === id
          ? {
              ...u,
              reset_requested: false,
              reset_requested_at: null,
              reset_token_active: true,
              reset_token_expires: r.data.expires_at,
            }
          : u
      )));
    } catch (e) {
      setError(e?.response?.data?.error || 'Failed to issue reset token');
    }
  };

  const handleCopyToken = async (token) => {
    try {
      await navigator.clipboard.writeText(token);
    } catch {
      // Clipboard may be unavailable (e.g. non-https origin); silently ignore.
    }
  };

  const handleDismissToken = (id) => {
    setResetTokens((prev) => {
      const next = { ...prev };
      delete next[id];
      return next;
    });
  };

  const handleDelete = async (id) => {
    if (id === currentUserId) {
      setError('You cannot delete your own account.');
      return;
    }
    if (!window.confirm('Delete this user?')) return;
    try {
      await axios.delete(`/api/users/${id}`);
      // Drop the row locally; avoids a full /api/users round trip.
      setUsers((prev) => prev.filter((u) => u.id !== id));
      setPendingRoles((prev) => { const n = { ...prev }; delete n[id]; return n; });
      setPendingActive((prev) => { const n = { ...prev }; delete n[id]; return n; });
      setResetTokens((prev) => { const n = { ...prev }; delete n[id]; return n; });
    } catch (e) {
      const code = e?.response?.data?.error;
      const msg = code === 'cannot_delete_self'
        ? 'You cannot delete your own account.'
        : (code || 'Failed to delete user');
      setError(msg);
    }
  };

  const dirtyCount = dirtyIds.size;
  const resetRequestedCount = useMemo(
    () => users.filter((u) => u.reset_requested).length,
    [users],
  );
  // Always sort users by id ascending — keeps the list stable regardless of
  // who currently has a pending password-reset request (the row highlight and
  // banner already draw the superuser's attention to those rows).
  const sortedUsers = useMemo(
    () => [...users].sort((a, b) => (a.id || 0) - (b.id || 0)),
    [users],
  );

  return (
    <div className="max-w-4xl mx-auto bg-white rounded-lg shadow p-4 sm:p-6">
      <h1 className="text-xl font-bold text-blue-600 mb-4">User management</h1>

      <form onSubmit={handleCreate} className="grid grid-cols-1 sm:grid-cols-5 gap-2 mb-6">
        <input
          className="border rounded px-2 py-1"
          placeholder="username"
          value={form.username}
          onChange={(e) => setForm({ ...form, username: e.target.value })}
          required
        />
        <input
          className="border rounded px-2 py-1"
          type="email"
          placeholder="email (optional)"
          value={form.email}
          onChange={(e) => setForm({ ...form, email: e.target.value })}
        />
        <input
          className="border rounded px-2 py-1"
          type="password"
          placeholder="password"
          value={form.password}
          onChange={(e) => setForm({ ...form, password: e.target.value })}
          required
        />
        <select
          className="border rounded px-2 py-1"
          value={form.role}
          onChange={(e) => setForm({ ...form, role: e.target.value })}
        >
          {ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
        </select>
        <button
          type="submit"
          disabled={busy}
          className="bg-blue-600 text-white rounded px-3 py-1 hover:bg-blue-700 disabled:opacity-50"
        >
          Create user
        </button>
      </form>

      {error && <div className="mb-3 text-sm text-red-600">{error}</div>}

      {resetRequestedCount > 0 && (
        <div className="mb-3 text-sm rounded border border-amber-300 bg-amber-50 text-amber-800 px-3 py-2">
          {resetRequestedCount} user{resetRequestedCount === 1 ? '' : 's'} requested a password reset. Click <b>issue token</b> on the highlighted row{resetRequestedCount === 1 ? '' : 's'} to mint a one-time token.
        </div>
      )}

      {loading ? (
        <div>Loading…</div>
      ) : (
        <>
          <div className="flex items-center justify-between mb-2">
            <div className="text-sm text-slate-500">
              {dirtyCount > 0
                ? `${dirtyCount} unsaved change${dirtyCount === 1 ? '' : 's'}`
                : 'No unsaved changes'}
            </div>
            <div className="space-x-2">
              <button
                onClick={handleDiscardAll}
                disabled={dirtyCount === 0 || savingAll}
                className="border rounded px-3 py-1 text-slate-600 hover:bg-slate-100 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                Discard
              </button>
              <button
                onClick={handleSaveAll}
                disabled={dirtyCount === 0 || savingAll}
                className="bg-blue-600 text-white rounded px-3 py-1 hover:bg-blue-700 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                {savingAll ? 'Saving…' : 'Save changes'}
              </button>
            </div>
          </div>
          <table className="w-full text-sm">
            <thead className="text-left text-slate-500 border-b">
              <tr>
                <th className="py-2">ID</th>
                <th>Username</th>
                <th>Email</th>
                <th>Role</th>
                <th>Active</th>
                <th>Created</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {sortedUsers.map((u) => {
                const effectiveRole = pendingRoles[u.id] ?? u.role;
                const effectiveActive = pendingActive[u.id] !== undefined ? pendingActive[u.id] : u.is_active;
                const rowDirty = dirtyIds.has(u.id);
                const tokenInfo = resetTokens[u.id];
                const requestedAt = u.reset_requested_at
                  ? u.reset_requested_at.replace('T', ' ').slice(0, 19)
                  : null;
                const rowBg = rowDirty
                  ? 'bg-yellow-50'
                  : (u.reset_requested ? 'bg-amber-50' : '');
                return (
                  <React.Fragment key={u.id}>
                    <tr className={`border-b last:border-b-0 ${rowBg}`}>
                      <td className="py-2">{u.id}</td>
                      <td>
                        <div className="flex items-center gap-2">
                          <span>{u.username}</span>
                          {u.reset_requested && (
                            <span
                              title={requestedAt ? `Requested at ${requestedAt} UTC` : 'Password reset requested'}
                              className="inline-flex items-center text-[10px] uppercase tracking-wide rounded px-1.5 py-0.5 bg-amber-200 text-amber-900"
                            >
                              reset requested
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="text-slate-600 break-all">{u.email || <span className="text-slate-400">—</span>}</td>
                      <td>
                        <select
                          value={effectiveRole}
                          onChange={(e) => handleRoleSelect(u.id, e.target.value)}
                          className="border rounded px-1 py-0.5"
                        >
                          {ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
                        </select>
                      </td>
                      <td>
                        <button
                          onClick={() => handleActiveToggle(u)}
                          className={`px-2 py-0.5 rounded ${effectiveActive ? 'bg-green-100 text-green-700' : 'bg-slate-200 text-slate-600'}`}
                        >
                          {effectiveActive ? 'active' : 'disabled'}
                        </button>
                      </td>
                      <td className="text-slate-500">{u.created_at?.replace('T', ' ').slice(0, 19)}</td>
                      <td className="space-x-2 text-right">
                        <button onClick={() => handleResetPassword(u.id)} className="text-blue-600 hover:underline">reset pw</button>
                        <button onClick={() => handleIssueResetToken(u.id)} className="text-blue-600 hover:underline">issue token</button>
                        {u.id !== currentUserId ? (
                          <button onClick={() => handleDelete(u.id)} className="text-red-600 hover:underline">delete</button>
                        ) : (
                          <span aria-hidden="true" className="invisible select-none">delete</span>
                        )}
                      </td>
                    </tr>
                    {tokenInfo && (
                      <tr className="border-b last:border-b-0 bg-amber-50">
                        <td colSpan={7} className="py-2 px-2">
                          <div className="flex items-start gap-2">
                            <div className="flex-1">
                              <div className="text-xs text-amber-700 mb-1">
                                One-time reset token for <b>{u.username}</b> &middot; expires {tokenInfo.expires_at?.replace('T', ' ').slice(0, 19)} UTC
                              </div>
                              <code className="block break-all text-xs bg-white border rounded px-2 py-1">{tokenInfo.token}</code>
                            </div>
                            <button
                              onClick={() => handleCopyToken(tokenInfo.token)}
                              className="border rounded px-2 py-1 text-xs hover:bg-white"
                            >
                              Copy
                            </button>
                            <button
                              onClick={() => handleDismissToken(u.id)}
                              className="border rounded px-2 py-1 text-xs hover:bg-white"
                            >
                              Dismiss
                            </button>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </>
      )}
    </div>
  );
};

export default UsersAdminPage;
